import React, { useState } from 'react';
import './HealthPage.css';

const HealthPage: React.FC = () => {
  // Menstrual cycle state
  const [cycleData, setCycleData] = useState<Array<{id: number, date: string, symptoms: string[], mood: string, flow: string}>>([]);
  const [showCycleModal, setShowCycleModal] = useState(false);
  const [newCycleEntry, setNewCycleEntry] = useState({date: '', symptoms: [''], mood: 'good', flow: 'normal'});

  // Medications state
  const [medications, setMedications] = useState<Array<{id: number, name: string, dosage: string, frequency: string, times: string[], startDate: string, endDate?: string}>>([]);
  const [showMedicationModal, setShowMedicationModal] = useState(false);
  const [newMedication, setNewMedication] = useState({name: '', dosage: '', frequency: 'daily', times: ['09:00'], startDate: '', endDate: ''});

  // Doctors and tests state
  const [appointments, setAppointments] = useState<Array<{id: number, doctor: string, specialty: string, date: string, time: string, notes: string, status: 'upcoming' | 'completed' | 'cancelled'}>>([]);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [newAppointment, setNewAppointment] = useState({doctor: '', specialty: '', date: '', time: '', notes: ''});

  // Nutrition and water state
  const [waterIntake, setWaterIntake] = useState<Array<{id: number, date: string, amount: number, goal: number}>>([]);
  const [meals, setMeals] = useState<Array<{id: number, date: string, type: 'breakfast' | 'lunch' | 'dinner' | 'snack', food: string, calories?: number}>>([]);
  const [showNutritionModal, setShowNutritionModal] = useState(false);
  const [newMeal, setNewMeal] = useState({type: 'breakfast' as 'breakfast' | 'lunch' | 'dinner' | 'snack', food: '', calories: ''});

  // Sleep state
  const [sleepData, setSleepData] = useState<Array<{id: number, date: string, bedTime: string, wakeTime: string, quality: 'excellent' | 'good' | 'fair' | 'poor', notes: string}>>([]);
  const [showSleepModal, setShowSleepModal] = useState(false);
  const [newSleepEntry, setNewSleepEntry] = useState({bedTime: '', wakeTime: '', quality: 'good' as 'excellent' | 'good' | 'fair' | 'poor', notes: ''});

  // Activity state
  const [activities, setActivities] = useState<Array<{id: number, date: string, type: string, duration: number, intensity: 'low' | 'medium' | 'high', calories?: number, notes: string}>>([]);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [newActivity, setNewActivity] = useState({type: '', duration: '', intensity: 'medium' as 'low' | 'medium' | 'high', calories: '', notes: ''});

  // Mood state
  const [moodEntries, setMoodEntries] = useState<Array<{id: number, date: string, mood: 'excellent' | 'good' | 'okay' | 'poor' | 'terrible', energy: number, stress: number, notes: string}>>([]);
  const [showMoodModal, setShowMoodModal] = useState(false);
  const [newMoodEntry, setNewMoodEntry] = useState({mood: 'good' as 'excellent' | 'good' | 'okay' | 'poor' | 'terrible', energy: 5, stress: 3, notes: ''});

  // Meditation state
  const [meditationSessions, setMeditationSessions] = useState<Array<{id: number, date: string, type: string, duration: number, notes: string}>>([]);
  const [showMeditationModal, setShowMeditationModal] = useState(false);
  const [newMeditationSession, setNewMeditationSession] = useState({type: '', duration: '', notes: ''});

  // Self-care state
  const [selfCareTasks, setSelfCareTasks] = useState<Array<{id: number, name: string, category: 'skincare' | 'hair' | 'nails' | 'body' | 'relaxation', frequency: string, lastDone?: string, nextDue?: string}>>([]);
  const [showSelfCareModal, setShowSelfCareModal] = useState(false);
  const [newSelfCareTask, setNewSelfCareTask] = useState({name: '', category: 'skincare' as 'skincare' | 'hair' | 'nails' | 'body' | 'relaxation', frequency: 'weekly'});

  // Optional sections visibility state
  const [enabledSections, setEnabledSections] = useState({
    medications: false,
    appointments: false,
    nutrition: false,
    activities: false,
    meditation: false,
    selfCare: false
  });

  // Helper functions
  const getTodayData = () => {
    const today = new Date().toISOString().split('T')[0];
    return {
      water: waterIntake.find(w => w.date === today),
      meals: meals.filter(m => m.date === today),
      sleep: sleepData.find(s => s.date === today),
      activities: activities.filter(a => a.date === today),
      mood: moodEntries.find(m => m.date === today),
      meditation: meditationSessions.filter(m => m.date === today)
    };
  };

  const todayData = getTodayData();

  // Function to toggle section visibility
  const toggleSection = (section: keyof typeof enabledSections) => {
    setEnabledSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="health-page">
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Здоровье</h1>
          <p className="page-subtitle">Следи за своим самочувствием и веди здоровый образ жизни</p>
        </div>
        
        <div className="page-content">
          <div className="section">
            <h2 className="section-title">Женское здоровье</h2>
            
            {/* Menstrual Cycle */}
            <div className="health-section">
              <div className="section-header">
                <h3>🌸 Менструальный цикл</h3>
                <button onClick={() => setShowCycleModal(true)} className="btn btn-primary">Добавить запись</button>
              </div>
              
              <div className="cycle-overview">
                {cycleData.length === 0 ? (
                  <div className="empty-state">
                    <p>Начните отслеживать свой цикл</p>
                  </div>
                ) : (
                  <div className="cycle-stats">
                    <div className="cycle-stat">
                      <span className="stat-number">{cycleData.length}</span>
                      <span className="stat-label">записей</span>
                    </div>
                    <div className="cycle-stat">
                      <span className="stat-number">
                        {cycleData.filter(c => c.flow === 'heavy').length}
                      </span>
                      <span className="stat-label">дней с обильным потоком</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Medications - Optional */}
            {enabledSections.medications && (
            <div className="health-section">
              <div className="section-header">
                <h3>💊 Прием лекарств</h3>
                <button onClick={() => setShowMedicationModal(true)} className="btn btn-primary">Добавить лекарство</button>
              </div>
              
              <div className="medications-list">
                {medications.length === 0 ? (
                  <div className="empty-state">
                    <p>Добавьте лекарства для отслеживания</p>
                  </div>
                ) : (
                  medications.map(med => (
                    <div key={med.id} className="medication-item">
                      <div className="med-info">
                        <h4>{med.name}</h4>
                        <p>{med.dosage} - {med.frequency}</p>
                        <span className="med-times">{med.times.join(', ')}</span>
                      </div>
                      <div className="med-status">
                        <span className="status-badge">Активно</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            )}

            {/* Doctors and Appointments - Optional */}
            {enabledSections.appointments && (
            <div className="health-section">
              <div className="section-header">
                <h3>🏥 Врачи и анализы</h3>
                <button onClick={() => setShowAppointmentModal(true)} className="btn btn-primary">Записаться</button>
              </div>
              
              <div className="appointments-list">
                {appointments.length === 0 ? (
                  <div className="empty-state">
                    <p>Нет запланированных приемов</p>
                  </div>
                ) : (
                  appointments.slice(0, 3).map(appointment => (
                    <div key={appointment.id} className={`appointment-item ${appointment.status}`}>
                      <div className="appointment-info">
                        <h4>{appointment.doctor}</h4>
                        <p>{appointment.specialty}</p>
                        <span className="appointment-date">{appointment.date} в {appointment.time}</span>
                      </div>
                      <span className={`appointment-status ${appointment.status}`}>
                        {appointment.status === 'upcoming' ? 'Запланирован' : 
                         appointment.status === 'completed' ? 'Завершен' : 'Отменен'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
            )}
          </div>

          <div className="section">
            <h2 className="section-title">Образ жизни</h2>
            
            {/* Nutrition and Water - Optional */}
            {enabledSections.nutrition && (
            <div className="health-section">
              <div className="section-header">
                <h3>💧 Вода и питание</h3>
                <button onClick={() => setShowNutritionModal(true)} className="btn btn-secondary">Добавить прием пищи</button>
              </div>
              
              <div className="water-tracker">
                <div className="water-progress">
                  <div className="water-circle">
                    <span className="water-amount">
                      {todayData.water ? todayData.water.amount : 0}мл
                    </span>
                    <span className="water-goal">
                      / {todayData.water ? todayData.water.goal : 2000}мл
                    </span>
                  </div>
                </div>
                <div className="water-buttons">
                  <button onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    const existing = waterIntake.find(w => w.date === today);
                    if (existing) {
                      setWaterIntake(waterIntake.map(w => 
                        w.date === today ? {...w, amount: w.amount + 250} : w
                      ));
                    } else {
                      setWaterIntake([...waterIntake, {
                        id: Date.now(),
                        date: today,
                        amount: 250,
                        goal: 2000
                      }]);
                    }
                  }} className="water-add-btn">+250мл</button>
                  <button onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    const existing = waterIntake.find(w => w.date === today);
                    if (existing) {
                      setWaterIntake(waterIntake.map(w => 
                        w.date === today ? {...w, amount: w.amount + 500} : w
                      ));
                    } else {
                      setWaterIntake([...waterIntake, {
                        id: Date.now(),
                        date: today,
                        amount: 500,
                        goal: 2000
                      }]);
                    }
                  }} className="water-add-btn">+500мл</button>
                </div>
              </div>
              
              <div className="meals-list">
                <h4>Приемы пищи сегодня:</h4>
                {todayData.meals.length === 0 ? (
                  <div className="empty-state">
                    <p>Добавьте приемы пищи</p>
                  </div>
                ) : (
                  todayData.meals.map(meal => (
                    <div key={meal.id} className="meal-item">
                      <span className="meal-type">{meal.type}</span>
                      <span className="meal-food">{meal.food}</span>
                      {meal.calories && <span className="meal-calories">{meal.calories} ккал</span>}
                    </div>
                  ))
                )}
              </div>
            </div>
            )}

            {/* Sleep */}
            <div className="health-section">
              <div className="section-header">
                <h3>😴 Сон и отдых</h3>
                <button onClick={() => setShowSleepModal(true)} className="btn btn-secondary">Записать сон</button>
              </div>
              
              <div className="sleep-overview">
                {todayData.sleep ? (
                  <div className="sleep-stats">
                    <div className="sleep-stat">
                      <span className="sleep-label">Время сна:</span>
                      <span className="sleep-value">
                        {todayData.sleep.bedTime} - {todayData.sleep.wakeTime}
                      </span>
                    </div>
                    <div className="sleep-stat">
                      <span className="sleep-label">Качество:</span>
                      <span className={`sleep-quality ${todayData.sleep.quality}`}>
                        {todayData.sleep.quality === 'excellent' ? 'Отлично' :
                         todayData.sleep.quality === 'good' ? 'Хорошо' :
                         todayData.sleep.quality === 'fair' ? 'Удовлетворительно' : 'Плохо'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>Запишите данные о сне</p>
                  </div>
                )}
              </div>
            </div>

            {/* Activity - Optional */}
            {enabledSections.activities && (
            <div className="health-section">
              <div className="section-header">
                <h3>🏃‍♀️ Активность</h3>
                <button onClick={() => setShowActivityModal(true)} className="btn btn-secondary">Добавить тренировку</button>
              </div>
              
              <div className="activity-overview">
                {todayData.activities.length === 0 ? (
                  <div className="empty-state">
                    <p>Добавьте физическую активность</p>
                  </div>
                ) : (
                  <div className="activities-list">
                    {todayData.activities.map(activity => (
                      <div key={activity.id} className="activity-item">
                        <div className="activity-info">
                          <h4>{activity.type}</h4>
                          <p>{activity.duration} мин • {activity.intensity}</p>
                          {activity.calories && <span className="activity-calories">{activity.calories} ккал</span>}
                        </div>
                        <span className={`activity-intensity ${activity.intensity}`}>
                          {activity.intensity === 'high' ? 'Высокая' :
                           activity.intensity === 'medium' ? 'Средняя' : 'Низкая'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          <div className="section">
            <h2 className="section-title">Самочувствие</h2>
            
            {/* Mood */}
            <div className="health-section">
              <div className="section-header">
                <h3>😊 Настроение</h3>
                <button onClick={() => setShowMoodModal(true)} className="btn btn-primary">Записать настроение</button>
              </div>
              
              <div className="mood-overview">
                {todayData.mood ? (
                  <div className="mood-stats">
                    <div className="mood-stat">
                      <span className="mood-label">Настроение:</span>
                      <span className={`mood-value ${todayData.mood.mood}`}>
                        {todayData.mood.mood === 'excellent' ? '😄 Отлично' :
                         todayData.mood.mood === 'good' ? '😊 Хорошо' :
                         todayData.mood.mood === 'okay' ? '😐 Нормально' :
                         todayData.mood.mood === 'poor' ? '😔 Плохо' : '😢 Ужасно'}
                      </span>
                    </div>
                    <div className="mood-stat">
                      <span className="mood-label">Энергия:</span>
                      <span className="mood-value">{todayData.mood.energy}/10</span>
                    </div>
                    <div className="mood-stat">
                      <span className="mood-label">Стресс:</span>
                      <span className="mood-value">{todayData.mood.stress}/10</span>
                    </div>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>Отметьте свое настроение</p>
                  </div>
                )}
              </div>
            </div>

            {/* Meditation - Optional */}
            {enabledSections.meditation && (
            <div className="health-section">
              <div className="section-header">
                <h3>🧘‍♀️ Медитация</h3>
                <button onClick={() => setShowMeditationModal(true)} className="btn btn-primary">Начать медитацию</button>
              </div>
              
              <div className="meditation-overview">
                {todayData.meditation.length === 0 ? (
                  <div className="empty-state">
                    <p>Начните практику медитации</p>
                  </div>
                ) : (
                  <div className="meditation-stats">
                    <div className="meditation-stat">
                      <span className="stat-number">{todayData.meditation.length}</span>
                      <span className="stat-label">сессий сегодня</span>
                    </div>
                    <div className="meditation-stat">
                      <span className="stat-number">
                        {todayData.meditation.reduce((sum, m) => sum + m.duration, 0)}
                      </span>
                      <span className="stat-label">минут медитации</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Self-care - Optional */}
            {enabledSections.selfCare && (
            <div className="health-section">
              <div className="section-header">
                <h3>💆‍♀️ Уход за собой</h3>
                <button onClick={() => setShowSelfCareModal(true)} className="btn btn-primary">Добавить процедуру</button>
              </div>
              
              <div className="selfcare-overview">
                {selfCareTasks.length === 0 ? (
                  <div className="empty-state">
                    <p>Запланируйте процедуры ухода</p>
                  </div>
                ) : (
                  <div className="selfcare-list">
                    {selfCareTasks.slice(0, 4).map(task => (
                      <div key={task.id} className="selfcare-item">
                        <div className="selfcare-info">
                          <h4>{task.name}</h4>
                          <p>{task.category} • {task.frequency}</p>
                        </div>
                        <div className="selfcare-status">
                          <span className="status-badge">Запланировано</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Today's Overview - Dynamic based on enabled sections */}
        <div className="today-overview">
          <h2>Сегодня</h2>
          <div className="overview-grid">
            {/* Always show basic sections */}
            <div className="overview-item">
              <span className="overview-icon">🌸</span>
              <div className="overview-info">
                <span className="overview-label">Менструальный цикл</span>
                <span className="overview-value">
                  {cycleData.length > 0 ? `${cycleData.length} записей` : 'Не отслеживается'}
                </span>
              </div>
            </div>
            <div className="overview-item">
              <span className="overview-icon">😊</span>
              <div className="overview-info">
                <span className="overview-label">Настроение</span>
                <span className="overview-value">
                  {todayData.mood ? 
                    (todayData.mood.mood === 'excellent' ? '😄 Отлично' :
                     todayData.mood.mood === 'good' ? '😊 Хорошо' :
                     todayData.mood.mood === 'okay' ? '😐 Нормально' :
                     todayData.mood.mood === 'poor' ? '😔 Плохо' : '😢 Ужасно') : 
                    'Не отмечено'}
                </span>
              </div>
            </div>
            <div className="overview-item">
              <span className="overview-icon">😴</span>
              <div className="overview-info">
                <span className="overview-label">Сон</span>
                <span className="overview-value">
                  {todayData.sleep ? `${todayData.sleep.bedTime} - ${todayData.sleep.wakeTime}` : 'Не записан'}
                </span>
              </div>
            </div>
            
            {/* Show optional sections only if enabled */}
            {enabledSections.medications && (
              <div className="overview-item">
                <span className="overview-icon">💊</span>
                <div className="overview-info">
                  <span className="overview-label">Лекарства</span>
                  <span className="overview-value">{medications.length} препаратов</span>
                </div>
              </div>
            )}
            
            {enabledSections.appointments && (
              <div className="overview-item">
                <span className="overview-icon">🏥</span>
                <div className="overview-info">
                  <span className="overview-label">Врачи</span>
                  <span className="overview-value">
                    {appointments.filter(a => a.status === 'upcoming').length} приемов
                  </span>
                </div>
              </div>
            )}
            
            {enabledSections.nutrition && (
              <>
                <div className="overview-item">
                  <span className="overview-icon">💧</span>
                  <div className="overview-info">
                    <span className="overview-label">Вода</span>
                    <span className="overview-value">
                      {todayData.water ? `${todayData.water.amount}/${todayData.water.goal} мл` : '0/2000 мл'}
                    </span>
                  </div>
                </div>
                <div className="overview-item">
                  <span className="overview-icon">🍽️</span>
                  <div className="overview-info">
                    <span className="overview-label">Приемы пищи</span>
                    <span className="overview-value">{todayData.meals.length}/3</span>
                  </div>
                </div>
              </>
            )}
            
            {enabledSections.activities && (
              <div className="overview-item">
                <span className="overview-icon">🏃‍♀️</span>
                <div className="overview-info">
                  <span className="overview-label">Активность</span>
                  <span className="overview-value">{todayData.activities.length} тренировок</span>
                </div>
              </div>
            )}
            
            {enabledSections.meditation && (
              <div className="overview-item">
                <span className="overview-icon">🧘‍♀️</span>
                <div className="overview-info">
                  <span className="overview-label">Медитация</span>
                  <span className="overview-value">{todayData.meditation.length} сессий</span>
                </div>
              </div>
            )}
            
            {enabledSections.selfCare && (
              <div className="overview-item">
                <span className="overview-icon">💆‍♀️</span>
                <div className="overview-info">
                  <span className="overview-label">Уход за собой</span>
                  <span className="overview-value">{selfCareTasks.length} процедур</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sections Manager - Moved to bottom */}
        <div className="sections-manager">
          <h3>Настройка разделов</h3>
          <p>Выберите разделы, которые хотите видеть в своем разделе здоровья:</p>
          <div className="sections-toggles">
            <label className="section-toggle">
              <input
                type="checkbox"
                checked={enabledSections.medications}
                onChange={() => toggleSection('medications')}
              />
              <span>💊 Прием лекарств</span>
            </label>
            <label className="section-toggle">
              <input
                type="checkbox"
                checked={enabledSections.appointments}
                onChange={() => toggleSection('appointments')}
              />
              <span>🏥 Врачи и анализы</span>
            </label>
            <label className="section-toggle">
              <input
                type="checkbox"
                checked={enabledSections.nutrition}
                onChange={() => toggleSection('nutrition')}
              />
              <span>💧 Вода и питание</span>
            </label>
            <label className="section-toggle">
              <input
                type="checkbox"
                checked={enabledSections.activities}
                onChange={() => toggleSection('activities')}
              />
              <span>🏃‍♀️ Активность</span>
            </label>
            <label className="section-toggle">
              <input
                type="checkbox"
                checked={enabledSections.meditation}
                onChange={() => toggleSection('meditation')}
              />
              <span>🧘‍♀️ Медитация</span>
            </label>
            <label className="section-toggle">
              <input
                type="checkbox"
                checked={enabledSections.selfCare}
                onChange={() => toggleSection('selfCare')}
              />
              <span>💆‍♀️ Уход за собой</span>
            </label>
          </div>
        </div>
      </div>

      {/* Modals */}
      {/* Cycle Modal */}
      {showCycleModal && (
        <div className="modal-overlay" onClick={() => setShowCycleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Запись цикла</h3>
              <button onClick={() => setShowCycleModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="date"
                value={newCycleEntry.date}
                onChange={(e) => setNewCycleEntry({...newCycleEntry, date: e.target.value})}
                className="modal-input"
              />
              <select
                value={newCycleEntry.flow}
                onChange={(e) => setNewCycleEntry({...newCycleEntry, flow: e.target.value})}
                className="modal-select"
              >
                <option value="light">Слабый</option>
                <option value="normal">Обычный</option>
                <option value="heavy">Обильный</option>
              </select>
              <select
                value={newCycleEntry.mood}
                onChange={(e) => setNewCycleEntry({...newCycleEntry, mood: e.target.value})}
                className="modal-select"
              >
                <option value="good">Хорошее</option>
                <option value="okay">Нормальное</option>
                <option value="bad">Плохое</option>
              </select>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCycleModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newCycleEntry.date) {
                  setCycleData([...cycleData, {
                    id: Date.now(),
                    date: newCycleEntry.date,
                    symptoms: newCycleEntry.symptoms.filter(s => s.trim()),
                    mood: newCycleEntry.mood,
                    flow: newCycleEntry.flow
                  }]);
                  setNewCycleEntry({date: '', symptoms: [''], mood: 'good', flow: 'normal'});
                  setShowCycleModal(false);
                }
              }} className="btn btn-primary">Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* Medication Modal */}
      {showMedicationModal && (
        <div className="modal-overlay" onClick={() => setShowMedicationModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить лекарство</h3>
              <button onClick={() => setShowMedicationModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название лекарства"
                value={newMedication.name}
                onChange={(e) => setNewMedication({...newMedication, name: e.target.value})}
                className="modal-input"
              />
              <input
                type="text"
                placeholder="Дозировка"
                value={newMedication.dosage}
                onChange={(e) => setNewMedication({...newMedication, dosage: e.target.value})}
                className="modal-input"
              />
              <select
                value={newMedication.frequency}
                onChange={(e) => setNewMedication({...newMedication, frequency: e.target.value})}
                className="modal-select"
              >
                <option value="daily">Ежедневно</option>
                <option value="weekly">Еженедельно</option>
                <option value="as-needed">По необходимости</option>
              </select>
              <input
                type="date"
                value={newMedication.startDate}
                onChange={(e) => setNewMedication({...newMedication, startDate: e.target.value})}
                className="modal-input"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowMedicationModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newMedication.name && newMedication.startDate) {
                  setMedications([...medications, {
                    id: Date.now(),
                    name: newMedication.name,
                    dosage: newMedication.dosage,
                    frequency: newMedication.frequency,
                    times: newMedication.times,
                    startDate: newMedication.startDate,
                    endDate: newMedication.endDate
                  }]);
                  setNewMedication({name: '', dosage: '', frequency: 'daily', times: ['09:00'], startDate: '', endDate: ''});
                  setShowMedicationModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Modal */}
      {showAppointmentModal && (
        <div className="modal-overlay" onClick={() => setShowAppointmentModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Записаться к врачу</h3>
              <button onClick={() => setShowAppointmentModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Имя врача"
                value={newAppointment.doctor}
                onChange={(e) => setNewAppointment({...newAppointment, doctor: e.target.value})}
                className="modal-input"
              />
              <input
                type="text"
                placeholder="Специализация"
                value={newAppointment.specialty}
                onChange={(e) => setNewAppointment({...newAppointment, specialty: e.target.value})}
                className="modal-input"
              />
              <input
                type="date"
                value={newAppointment.date}
                onChange={(e) => setNewAppointment({...newAppointment, date: e.target.value})}
                className="modal-input"
              />
              <input
                type="time"
                value={newAppointment.time}
                onChange={(e) => setNewAppointment({...newAppointment, time: e.target.value})}
                className="modal-input"
              />
              <textarea
                placeholder="Заметки"
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
                className="modal-textarea"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowAppointmentModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newAppointment.doctor && newAppointment.date) {
                  setAppointments([...appointments, {
                    id: Date.now(),
                    doctor: newAppointment.doctor,
                    specialty: newAppointment.specialty,
                    date: newAppointment.date,
                    time: newAppointment.time,
                    notes: newAppointment.notes,
                    status: 'upcoming'
                  }]);
                  setNewAppointment({doctor: '', specialty: '', date: '', time: '', notes: ''});
                  setShowAppointmentModal(false);
                }
              }} className="btn btn-primary">Записаться</button>
            </div>
          </div>
        </div>
      )}

      {/* Nutrition Modal */}
      {showNutritionModal && (
        <div className="modal-overlay" onClick={() => setShowNutritionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить прием пищи</h3>
              <button onClick={() => setShowNutritionModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <select
                value={newMeal.type}
                onChange={(e) => setNewMeal({...newMeal, type: e.target.value as any})}
                className="modal-select"
              >
                <option value="breakfast">Завтрак</option>
                <option value="lunch">Обед</option>
                <option value="dinner">Ужин</option>
                <option value="snack">Перекус</option>
              </select>
              <input
                type="text"
                placeholder="Что ели"
                value={newMeal.food}
                onChange={(e) => setNewMeal({...newMeal, food: e.target.value})}
                className="modal-input"
              />
              <input
                type="number"
                placeholder="Калории (опционально)"
                value={newMeal.calories}
                onChange={(e) => setNewMeal({...newMeal, calories: e.target.value})}
                className="modal-input"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowNutritionModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newMeal.food) {
                  setMeals([...meals, {
                    id: Date.now(),
                    date: new Date().toISOString().split('T')[0],
                    type: newMeal.type,
                    food: newMeal.food,
                    calories: newMeal.calories ? parseInt(newMeal.calories) : undefined
                  }]);
                  setNewMeal({type: 'breakfast', food: '', calories: ''});
                  setShowNutritionModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

      {/* Sleep Modal */}
      {showSleepModal && (
        <div className="modal-overlay" onClick={() => setShowSleepModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Записать сон</h3>
              <button onClick={() => setShowSleepModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="time"
                placeholder="Время засыпания"
                value={newSleepEntry.bedTime}
                onChange={(e) => setNewSleepEntry({...newSleepEntry, bedTime: e.target.value})}
                className="modal-input"
              />
              <input
                type="time"
                placeholder="Время пробуждения"
                value={newSleepEntry.wakeTime}
                onChange={(e) => setNewSleepEntry({...newSleepEntry, wakeTime: e.target.value})}
                className="modal-input"
              />
              <select
                value={newSleepEntry.quality}
                onChange={(e) => setNewSleepEntry({...newSleepEntry, quality: e.target.value as any})}
                className="modal-select"
              >
                <option value="excellent">Отличное</option>
                <option value="good">Хорошее</option>
                <option value="fair">Удовлетворительное</option>
                <option value="poor">Плохое</option>
              </select>
              <textarea
                placeholder="Заметки о сне"
                value={newSleepEntry.notes}
                onChange={(e) => setNewSleepEntry({...newSleepEntry, notes: e.target.value})}
                className="modal-textarea"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowSleepModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newSleepEntry.bedTime && newSleepEntry.wakeTime) {
                  setSleepData([...sleepData, {
                    id: Date.now(),
                    date: new Date().toISOString().split('T')[0],
                    bedTime: newSleepEntry.bedTime,
                    wakeTime: newSleepEntry.wakeTime,
                    quality: newSleepEntry.quality,
                    notes: newSleepEntry.notes
                  }]);
                  setNewSleepEntry({bedTime: '', wakeTime: '', quality: 'good', notes: ''});
                  setShowSleepModal(false);
                }
              }} className="btn btn-primary">Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Modal */}
      {showActivityModal && (
        <div className="modal-overlay" onClick={() => setShowActivityModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить тренировку</h3>
              <button onClick={() => setShowActivityModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Тип активности"
                value={newActivity.type}
                onChange={(e) => setNewActivity({...newActivity, type: e.target.value})}
                className="modal-input"
              />
              <input
                type="number"
                placeholder="Длительность (минуты)"
                value={newActivity.duration}
                onChange={(e) => setNewActivity({...newActivity, duration: e.target.value})}
                className="modal-input"
              />
              <select
                value={newActivity.intensity}
                onChange={(e) => setNewActivity({...newActivity, intensity: e.target.value as any})}
                className="modal-select"
              >
                <option value="low">Низкая</option>
                <option value="medium">Средняя</option>
                <option value="high">Высокая</option>
              </select>
              <input
                type="number"
                placeholder="Калории (опционально)"
                value={newActivity.calories}
                onChange={(e) => setNewActivity({...newActivity, calories: e.target.value})}
                className="modal-input"
              />
              <textarea
                placeholder="Заметки"
                value={newActivity.notes}
                onChange={(e) => setNewActivity({...newActivity, notes: e.target.value})}
                className="modal-textarea"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowActivityModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newActivity.type && newActivity.duration) {
                  setActivities([...activities, {
                    id: Date.now(),
                    date: new Date().toISOString().split('T')[0],
                    type: newActivity.type,
                    duration: parseInt(newActivity.duration),
                    intensity: newActivity.intensity,
                    calories: newActivity.calories ? parseInt(newActivity.calories) : undefined,
                    notes: newActivity.notes
                  }]);
                  setNewActivity({type: '', duration: '', intensity: 'medium', calories: '', notes: ''});
                  setShowActivityModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

      {/* Mood Modal */}
      {showMoodModal && (
        <div className="modal-overlay" onClick={() => setShowMoodModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Записать настроение</h3>
              <button onClick={() => setShowMoodModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <select
                value={newMoodEntry.mood}
                onChange={(e) => setNewMoodEntry({...newMoodEntry, mood: e.target.value as any})}
                className="modal-select"
              >
                <option value="excellent">😄 Отлично</option>
                <option value="good">😊 Хорошо</option>
                <option value="okay">😐 Нормально</option>
                <option value="poor">😔 Плохо</option>
                <option value="terrible">😢 Ужасно</option>
              </select>
              <div className="range-input">
                <label>Энергия: {newMoodEntry.energy}/10</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={newMoodEntry.energy}
                  onChange={(e) => setNewMoodEntry({...newMoodEntry, energy: parseInt(e.target.value)})}
                  className="modal-range"
                />
              </div>
              <div className="range-input">
                <label>Стресс: {newMoodEntry.stress}/10</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={newMoodEntry.stress}
                  onChange={(e) => setNewMoodEntry({...newMoodEntry, stress: parseInt(e.target.value)})}
                  className="modal-range"
                />
              </div>
              <textarea
                placeholder="Заметки о настроении"
                value={newMoodEntry.notes}
                onChange={(e) => setNewMoodEntry({...newMoodEntry, notes: e.target.value})}
                className="modal-textarea"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowMoodModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                setMoodEntries([...moodEntries, {
                  id: Date.now(),
                  date: new Date().toISOString().split('T')[0],
                  mood: newMoodEntry.mood,
                  energy: newMoodEntry.energy,
                  stress: newMoodEntry.stress,
                  notes: newMoodEntry.notes
                }]);
                setNewMoodEntry({mood: 'good', energy: 5, stress: 3, notes: ''});
                setShowMoodModal(false);
              }} className="btn btn-primary">Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* Meditation Modal */}
      {showMeditationModal && (
        <div className="modal-overlay" onClick={() => setShowMeditationModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Медитация</h3>
              <button onClick={() => setShowMeditationModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Тип медитации"
                value={newMeditationSession.type}
                onChange={(e) => setNewMeditationSession({...newMeditationSession, type: e.target.value})}
                className="modal-input"
              />
              <input
                type="number"
                placeholder="Длительность (минуты)"
                value={newMeditationSession.duration}
                onChange={(e) => setNewMeditationSession({...newMeditationSession, duration: e.target.value})}
                className="modal-input"
              />
              <textarea
                placeholder="Заметки о сессии"
                value={newMeditationSession.notes}
                onChange={(e) => setNewMeditationSession({...newMeditationSession, notes: e.target.value})}
                className="modal-textarea"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowMeditationModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newMeditationSession.type && newMeditationSession.duration) {
                  setMeditationSessions([...meditationSessions, {
                    id: Date.now(),
                    date: new Date().toISOString().split('T')[0],
                    type: newMeditationSession.type,
                    duration: parseInt(newMeditationSession.duration),
                    notes: newMeditationSession.notes
                  }]);
                  setNewMeditationSession({type: '', duration: '', notes: ''});
                  setShowMeditationModal(false);
                }
              }} className="btn btn-primary">Завершить</button>
            </div>
          </div>
        </div>
      )}

      {/* Self-care Modal */}
      {showSelfCareModal && (
        <div className="modal-overlay" onClick={() => setShowSelfCareModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить процедуру ухода</h3>
              <button onClick={() => setShowSelfCareModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название процедуры"
                value={newSelfCareTask.name}
                onChange={(e) => setNewSelfCareTask({...newSelfCareTask, name: e.target.value})}
                className="modal-input"
              />
              <select
                value={newSelfCareTask.category}
                onChange={(e) => setNewSelfCareTask({...newSelfCareTask, category: e.target.value as any})}
                className="modal-select"
              >
                <option value="skincare">Уход за кожей</option>
                <option value="hair">Уход за волосами</option>
                <option value="nails">Маникюр/педикюр</option>
                <option value="body">Уход за телом</option>
                <option value="relaxation">Релаксация</option>
              </select>
              <select
                value={newSelfCareTask.frequency}
                onChange={(e) => setNewSelfCareTask({...newSelfCareTask, frequency: e.target.value})}
                className="modal-select"
              >
                <option value="daily">Ежедневно</option>
                <option value="weekly">Еженедельно</option>
                <option value="monthly">Ежемесячно</option>
              </select>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowSelfCareModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={() => {
                if (newSelfCareTask.name) {
                  setSelfCareTasks([...selfCareTasks, {
                    id: Date.now(),
                    name: newSelfCareTask.name,
                    category: newSelfCareTask.category,
                    frequency: newSelfCareTask.frequency
                  }]);
                  setNewSelfCareTask({name: '', category: 'skincare', frequency: 'weekly'});
                  setShowSelfCareModal(false);
                }
              }} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthPage;
